import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { message } from 'antd';
import cognitoService, { CognitoUser, CognitoAuthResult } from '../services/cognitoService';

interface CognitoAuthContextType {
  user: CognitoUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; challenge?: string; session?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  setNewPassword: (session: string, newPassword: string, username: string) => Promise<boolean>;
  verifyTOTP: (email: string, emailConfirmationToken: string, code: string) => Promise<boolean>;  // Added for compatibility
}

const CognitoAuthContext = createContext<CognitoAuthContextType | undefined>(undefined);

export const useCognitoAuth = () => {
  const context = useContext(CognitoAuthContext);
  if (context === undefined) {
    throw new Error('useCognitoAuth must be used within a CognitoAuthProvider');
  }
  return context;
};

interface CognitoAuthProviderProps {
  children: ReactNode;
}

export const CognitoAuthProvider: React.FC<CognitoAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<CognitoUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkSession = async () => {
      // Simplified approach: treat page refresh as fresh login
      // Clear any existing tokens and require fresh authentication
      
      // Clear all stored authentication data
      await cognitoService.logout();
      setUser(null);
      setIsLoading(false);
    };
    
    checkSession();
  }, []);

  useEffect(() => {
    // Listen for user updates from other components
    const handleUserUpdate = (event: CustomEvent) => {
      setUser(event.detail);
    };

    window.addEventListener('userUpdated', handleUserUpdate as EventListener);
    
    return () => {
      window.removeEventListener('userUpdated', handleUserUpdate as EventListener);
    };
  }, []);

  const login = async (username: string, password: string): Promise<{ success: boolean; challenge?: string; session?: string }> => {
    try {
      const result: CognitoAuthResult = await cognitoService.authenticateUser(username, password);
      
      if (result.success && result.user) {
        setUser(result.user);
        message.success('Login realizado com sucesso!');
        return { success: true };
      } else if (result.challenge === 'NEW_PASSWORD_REQUIRED') {
        // Handle new password challenge
        message.info('Nova palavra-passe necessária');
        return { 
          success: true, 
          challenge: result.challenge, 
          session: result.session 
        };
      } else {
        message.error(result.message || 'Erro no login. Tente novamente.');
        return { success: false };
      }
    } catch (error: any) {
      message.error('Erro no login. Tente novamente.');
      return { success: false };
    }
  };

  const setNewPassword = async (session: string, newPassword: string, username: string): Promise<boolean> => {
    try {
      const result: CognitoAuthResult = await cognitoService.setNewPassword(session, newPassword, username);
      
      if (result.success && result.user) {
        setUser(result.user);
        message.success('Nova palavra-passe definida com sucesso!');
        return true;
      } else {
        message.error(result.message || 'Erro ao definir nova palavra-passe');
        return false;
      }
    } catch (error: any) {
      message.error('Erro ao definir nova palavra-passe');
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await cognitoService.logout();
      setUser(null);
      message.success('Sessão terminada com sucesso');
    } catch (error) {
      // Clear local state even if logout request fails
      setUser(null);
      message.success('Sessão terminada com sucesso');
    }
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const currentUser = await cognitoService.getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
      } else {
        // Fallback to stored user
        const storedUser = cognitoService.getStoredUser();
        if (storedUser) {
          setUser(storedUser);
        } else {
          setUser(null);
        }
      }
    } catch (error) {
      setUser(null);
    }
  };

  const verifyTOTP = async (email: string, emailConfirmationToken: string, code: string): Promise<boolean> => {
    // Cognito doesn't use TOTP in the same way, so this is a placeholder
    // In a real implementation, you might handle MFA challenges here
    message.warning('TOTP verification not implemented for Cognito authentication');
    return false;
  };

  const value: CognitoAuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshUser,
    setNewPassword,
    verifyTOTP,
  };

  return (
    <CognitoAuthContext.Provider value={value}>
      {children}
    </CognitoAuthContext.Provider>
  );
};
